import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { Input } from '@shared/components/ui/input';
import { Spinner } from '@shared/components/ui/Spinner';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { faPlus, faPrint, faTrash, faUser, faUsers } from '@fortawesome/pro-light-svg-icons';
import { UbicacionesIcon } from '@shared/components/ui/icons/warehouse.icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackingStationsApiAdapter } from '../../adapters/PackingStationsApiAdapter';
import { useManagePackingStationRelations } from '../../hooks/useManagePackingStationRelations';
import { PackingStationDetail, type PackingStationRelationsTabKey } from '../../models/PackingStation';
import { Service } from '../../services/Service';
import { resolvePackingStationLocationCode } from '../../utils/resolvePackingStationLocationCode';
import { LocationLabelPrintMenu } from '../interaction/LocationLabelPrintMenu';

export interface PackingStationRelationsPanelProps {
  stationId: number;
  detail: PackingStationDetail | null;
  isLoadingDetail: boolean;
  onInvalidate: () => void;
  /** Pestaña inicial al abrir el detalle (p. ej. desde una columna de la tabla) */
  initialTab?: PackingStationRelationsTabKey;
}

export const PackingStationRelationsPanel = ({
  stationId,
  detail,
  isLoadingDetail,
  onInvalidate,
  initialTab = 'printers',
}: PackingStationRelationsPanelProps) => {
  const { t } = useTranslation('packingStationsModule');
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const [activeTab, setActiveTab] = useState<PackingStationRelationsTabKey>(initialTab);
  const [selectedPrinterId, setSelectedPrinterId] = useState('');
  const [selectedPrinterType, setSelectedPrinterType] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedLocationCode, setSelectedLocationCode] = useState('');

  const printerTypeOptions = [
    { label: '10x15', value: '10x15' },
    { label: 'A4', value: 'a4' },
  ];

  useEffect(() => {
    setActiveTab(initialTab);
    setSelectedPrinterId('');
    setSelectedPrinterType('');
    setSelectedClientId('');
    setSelectedLocationCode('');
  }, [stationId, initialTab]);

  const {
    addPrinter,
    removePrinter,
    addClient,
    removeClient,
    addLocation,
    isLoading: isManaging,
  } = useManagePackingStationRelations(stationId, onInvalidate);
  const isRelationsSubmitting = isManaging;

  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new PackingStationsApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  const { data: printers = [] } = useQuery({
    queryKey: ['printers-for-packing-station'],
    queryFn: () => service.getPrinters(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: teamsChild = [] } = useQuery({
    queryKey: ['teams-child-for-packing-station'],
    queryFn: () => service.getTeamsChild(),
    staleTime: 5 * 60 * 1000,
  });

  const printerOptions = printers.map((p) => ({
    label: p.custom_name || p.name,
    value: String(p.id),
  }));

  const clientOptions = teamsChild.map((c) => ({
    label: c.name,
    value: String(c.id),
  }));

  const handleAddPrinter = async (printerId: string, printerType: string) => {
    if (!printerId || !printerType) return;
    const found = printers.find((printer) => String(printer.id) === printerId);
    if (!found) return;
    const printerCode = found.code?.trim() || String(found.id);
    await addPrinter(printerCode, printerType as '10x15' | 'a4', found.id);
    setSelectedPrinterId('');
    setSelectedPrinterType('');
  };

  const handleAddClient = async (clientId: string) => {
    if (!clientId) return;
    await addClient(Number(clientId));
    setSelectedClientId('');
  };

  const handleAddLocation = async () => {
    const code = selectedLocationCode.trim();
    if (!code) return;
    await addLocation(code);
    setSelectedLocationCode('');
  };

  // Abre modal de confirmación para cualquier acción de eliminar
  const confirmRemove = (onConfirm: () => void, entityName: string) => {
    confirmModalRef.current?.open(
      onConfirm,
      undefined,
      t('detail.confirmRemove.title'),
      t('detail.confirmRemove.message', { entity: entityName }),
      t('detail.confirmRemove.confirmButton'),
      t('detail.confirmRemove.cancelButton'),
    );
  };

  const tabs: { key: PackingStationRelationsTabKey; label: string; tabIcon: ReactNode }[] = [
    {
      key: 'printers',
      label: t('detail.tabs.printers'),
      tabIcon: <FontAwesomeIcon icon={faPrint} className="w-3.5 h-3.5" />,
    },
    {
      key: 'clients',
      label: t('detail.tabs.clients'),
      tabIcon: <FontAwesomeIcon icon={faUsers} className="w-3.5 h-3.5" />,
    },
    {
      key: 'locations',
      label: t('detail.tabs.locations'),
      tabIcon: <UbicacionesIcon className="w-3.5 h-3.5" />,
    },
  ];

  if (isLoadingDetail && !detail) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex w-full border-b border-lines gap-0">
        {tabs.map((tab) => {
          const useTitleText = true;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.key
                  ? useTitleText
                    ? 'border-primary text-title'
                    : 'border-primary text-primary'
                  : useTitleText
                    ? 'border-transparent text-title hover:opacity-90'
                    : 'border-transparent text-complementary-text hover:text-title'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.tabIcon}
              {tab.label}
            </button>
          );
        })}
      </div>

      <div
        className={`transition-opacity ${isRelationsSubmitting ? 'opacity-60' : 'opacity-100'}`}
        aria-busy={isRelationsSubmitting}
      >
        {activeTab === 'printers' && (
          <div className="space-y-4">
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[160px]">
                <label className="text-xs font-medium text-complementary-text block mb-1">
                  {t('detail.printers.selectPrinter')}
                </label>
                <Combobox
                  options={printerOptions}
                  value={selectedPrinterId}
                  setValue={(value) => {
                    setSelectedPrinterId(value);
                    if (selectedPrinterType) {
                      void handleAddPrinter(value, selectedPrinterType);
                    }
                  }}
                  placeholder={t('detail.printers.selectPrinterPlaceholder')}
                  disabled={isRelationsSubmitting}
                  dataTestId="select-printer"
                />
              </div>
              <div className="w-32">
                <label className="text-xs font-medium text-complementary-text block mb-1">
                  {t('detail.printers.selectType')}
                </label>
                <Combobox
                  options={printerTypeOptions}
                  value={selectedPrinterType}
                  setValue={(value) => {
                    setSelectedPrinterType(value);
                    if (selectedPrinterId) {
                      void handleAddPrinter(selectedPrinterId, value);
                    }
                  }}
                  placeholder={t('detail.printers.selectTypePlaceholder')}
                  disabled={isRelationsSubmitting}
                  dataTestId="select-printer-type"
                />
              </div>
            </div>

            <div className="space-y-2">
              {detail?.printers?.map((printer) => (
                <div
                  key={printer.id}
                  className="flex min-h-10 items-center justify-between gap-2 rounded-md border border-lines px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faPrint} className="w-3.5 h-3.5 text-icons" />
                    <span className="text-sm text-title">
                      {printer.name || t('detail.printers.printerLabel', { id: printer.printer_id })}
                    </span>
                    <Badge variant="outline" className="text-xs font-mono">
                      {printer.type}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      confirmRemove(
                        () => void removePrinter(printer.id),
                        printer.name || t('detail.confirmRemove.entities.printer', { id: printer.printer_id }),
                      )
                    }
                    disabled={isRelationsSubmitting}
                    data-testid={`remove-printer-${printer.id}`}
                  >
                    <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5 text-error" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'clients' && (
          <div className="space-y-4">
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-complementary-text block mb-1">
                  {t('detail.clients.selectClient')}
                </label>
                <Combobox
                  options={clientOptions}
                  value={selectedClientId}
                  setValue={(value) => {
                    setSelectedClientId(value);
                    void handleAddClient(value);
                  }}
                  placeholder={t('detail.clients.selectClientPlaceholder')}
                  disabled={isRelationsSubmitting}
                  dataTestId="select-client"
                />
              </div>
            </div>

            <div className="space-y-2">
              {detail?.clients?.map((client) => (
                <div
                  key={client.id}
                  className="flex min-h-10 items-center justify-between gap-2 rounded-md border border-lines px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faUser} className="w-3.5 h-3.5 text-icons" />
                    <span className="text-sm text-title">
                      {client.name || t('detail.clients.clientLabel', { id: client.tenant_external_id })}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      confirmRemove(
                        () => void removeClient(client.tenant_external_id),
                        client.name || t('detail.confirmRemove.entities.client', { id: client.tenant_external_id }),
                      )
                    }
                    disabled={isRelationsSubmitting}
                    data-testid={`remove-client-${client.id}`}
                  >
                    <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5 text-error" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'locations' && (
          <div className="space-y-4">
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-complementary-text block mb-1">
                  {t('detail.locations.codeLabel')}
                </label>
                <Input
                  type="text"
                  value={selectedLocationCode}
                  onChange={(e) => setSelectedLocationCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAddLocation();
                    }
                  }}
                  placeholder={t('detail.locations.codePlaceholder')}
                  disabled={isRelationsSubmitting}
                  data-testid="location-code-input"
                />
              </div>
              <Button
                variant="primary"
                size="lg"
                type="button"
                className="h-10 w-10 shrink-0 p-0"
                onClick={() => void handleAddLocation()}
                disabled={!selectedLocationCode.trim() || isRelationsSubmitting}
                data-testid="add-location-button"
              >
                <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
              </Button>
            </div>

            {/* Lista de ubicaciones ya asociadas al puesto */}
            {detail?.locations && detail.locations.length > 0 && (
              <div className="flex justify-end">
                <LocationLabelPrintMenu
                  variant="button"
                  codes={detail.locations.map((loc) => resolvePackingStationLocationCode(loc)).filter(Boolean)}
                  dataTestId="print-all-location-labels"
                />
              </div>
            )}
            <div className="space-y-2">
              {detail?.locations?.map((location) => {
                const locationCode = resolvePackingStationLocationCode(location) || '—';
                const packaging = location.packaging;
                const printableCode = locationCode !== '—' ? locationCode : '';

                return (
                  <div
                    key={location.id}
                    className="flex min-h-10 items-center justify-between gap-3 rounded-md border border-lines px-3 py-2"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <UbicacionesIcon className="w-3.5 h-3.5 shrink-0 text-icons" />
                      <span className="text-sm font-mono text-title">{locationCode}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <div className="min-w-0 text-right">
                        {packaging ? (
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="truncate text-sm text-title" title={packaging.name}>
                              {packaging.name}
                            </span>
                            <span className="text-xs text-complementary-text">
                              {packaging.code}
                              {typeof packaging.stock === 'number' && (
                                <>
                                  {' · '}
                                  {t('detail.locations.stock', { count: packaging.stock })}
                                </>
                              )}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-complementary-text">{t('detail.locations.noProduct')}</span>
                        )}
                      </div>
                      {printableCode && (
                        <LocationLabelPrintMenu
                          codes={[printableCode]}
                          dataTestId={`print-location-label-${location.id}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <ConfirmModal ref={confirmModalRef} />
    </div>
  );
};
